import React, { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import useSubscriptionRestricted from "../../hooks/useSubscriptionRestricted";
import useThemeColor from "../../hooks/useThemeColor";
import { logout } from "../../app/auth/authSlice";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import moment from "moment";
import { validateImageFile, MAX_IMAGE_SIZE_LABEL } from "../../utils/mediaImageUtils";
import "./companyBilling.scss";

const apiBaseUrl =
  process.env.REACT_APP_Environment === "development"
    ? process.env.REACT_APP_API_URL
    : process.env.REACT_APP_API_URL_Production;

/** Stack order: invoice details < pay < upload proof (nested from details). */
const MODAL_Z_PAY = 10400;
const MODAL_Z_INVOICE_DETAILS = 10450;
const MODAL_Z_UPLOAD_PROOF = 10550;

const txnField = (txn, ...keys) => {
  if (!txn) return null;
  for (const key of keys) {
    const val = txn[key];
    if (val != null && val !== "") return val;
  }
  return null;
};

const isBankTransfer = (txn) =>
  (txn?.paymentMethod || "").trim().toLowerCase() === "bank transfer";

function BillingOnlyHeader({ profileData, themeColor, onLogout }) {
  const companyName =
    profileData?.compName ?? profileData?.CompName ?? "Your account";

  return (
    <header className="billing-only-header">
      <div className="billing-only-header__inner">
        <div className="billing-only-header__brand">
          <span className="billing-only-header__logo" style={{ color: themeColor }}>
            <i className="fa fa-receipt" aria-hidden />
          </span>
          <div>
            <div className="billing-only-header__title">Subscription billing</div>
            <div className="billing-only-header__subtitle">{companyName}</div>
          </div>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onLogout}>
          <i className="fa fa-sign-out-alt me-2" aria-hidden />
          Log out
        </button>
      </div>
    </header>
  );
}

function SupportContactBlock({ supportInfo, themeColor }) {
  const email = (supportInfo?.supportEmail || "").trim();
  const phone = (supportInfo?.supportPhone || "").trim();
  const title = (supportInfo?.supportTitle || "").trim() || "Contact support";
  const message = (supportInfo?.supportMessage || "").trim();
  const hours = (supportInfo?.supportHours || "").trim();

  if (!email && !phone) return null;

  return (
    <div className="billing-support-contact card border-0 shadow-sm">
      <div className="card-body p-3 p-md-4">
        <div className="d-flex align-items-start gap-3">
          <div
            className="billing-support-contact__icon flex-shrink-0"
            style={{ backgroundColor: `${themeColor}18`, color: themeColor }}
          >
            <i className="fa fa-headset" aria-hidden />
          </div>
          <div className="flex-grow-1 text-start">
            <h6 className="fw-bold mb-1">{title}</h6>
            {message ? <p className="text-muted small mb-2">{message}</p> : null}
            <div className="d-flex flex-column gap-2">
              {email ? (
                <a className="billing-support-contact__link" href={`mailto:${email}`}>
                  <i className="fa fa-envelope me-2" aria-hidden />
                  {email}
                </a>
              ) : null}
              {phone ? (
                <a className="billing-support-contact__link" href={`tel:${phone.replace(/\s+/g, "")}`}>
                  <i className="fa fa-phone me-2" aria-hidden />
                  {phone}
                </a>
              ) : null}
            </div>
            {hours ? <p className="text-muted small mb-0 mt-2">Hours: {hours}</p> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function BranchBillingRestrictedMessage({
  profileData,
  themeColor,
  isRestricted,
  onLogout,
  supportInfo,
}) {
  const branchName =
    profileData?.compName ?? profileData?.CompName ?? "your branch";

  return (
    <div
      className={`company-billing-page${
        isRestricted ? " company-billing-page--restricted" : ""
      }`}
    >
      {isRestricted && (
        <BillingOnlyHeader
          profileData={profileData}
          themeColor={themeColor}
          onLogout={onLogout}
        />
      )}

      <div className="container-fluid px-4 py-4">
        {!isRestricted && (
          <div className="d-flex align-items-center mb-3">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Home</Link>
              </li>
              <li className="breadcrumb-item">Administration</li>
              <li className="breadcrumb-item active">Billing</li>
            </ol>
          </div>
        )}

        <div className="row justify-content-center">
          <div className="col-12 col-lg-8 col-xl-7">
            <div className="card billing-branch-restricted-card border-0 shadow-sm">
              <div className="card-body p-4 p-md-5 text-center">
                <div
                  className="billing-branch-restricted-icon mx-auto mb-4"
                  style={{
                    backgroundColor: `${themeColor}18`,
                    color: themeColor,
                  }}
                >
                  <i className="fa-solid fa-building-lock fa-2x" aria-hidden />
                </div>
                <h4 className="fw-bold mb-3">
                  {isRestricted
                    ? "Subscription access has ended"
                    : "Billing is managed by your main company"}
                </h4>
                {isRestricted ? (
                  <div className="alert alert-danger text-start mb-4" role="alert">
                    <i className="fa fa-exclamation-triangle me-2" aria-hidden />
                    Your restaurant subscription and grace period have ended. Billing can only be
                    paid from the <strong>main company</strong> account. Contact your head office or
                    support below to restore access.
                  </div>
                ) : (
                  <p className="text-muted mb-3 lead-sm">
                    Subscription invoices and payments can only be viewed and paid from your{" "}
                    <strong>main restaurant or head office</strong> account—not from a branch login.
                  </p>
                )}
                <p className="mb-4">
                  You are currently signed in to <strong>{branchName}</strong>. Please contact your
                  main company or restaurant administrator if you need help with billing, subscription
                  access, or invoice payment.
                </p>

                <div className="mb-4">
                  <SupportContactBlock supportInfo={supportInfo} themeColor={themeColor} />
                </div>

                <div className="d-flex flex-wrap justify-content-center gap-2">
                  {isRestricted ? (
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-4"
                      onClick={onLogout}
                    >
                      <i className="fa fa-sign-out-alt me-2" aria-hidden />
                      Log out
                    </button>
                  ) : (
                    <>
                      <Link to="/dashboard" className="btn btn-theme px-4">
                        <i className="fa fa-home me-2" aria-hidden />
                        Go to dashboard
                      </Link>
                      <Link to="/profile" className="btn btn-outline-secondary px-4">
                        <i className="fa fa-user me-2" aria-hidden />
                        View profile
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompanyBilling() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isChildBranch = useIsChildBranch();
  const { isRestricted } = useSubscriptionRestricted();
  const themeColor = useThemeColor();
  const [searchParams, setSearchParams] = useSearchParams();
  const profileData = useSelector((state) => state.auth.profileData);
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceTransactions, setInvoiceTransactions] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [supportInfo, setSupportInfo] = useState(null);

  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null);
  const [payTab, setPayTab] = useState("card");
  const [bankForm, setBankForm] = useState({
    amount: "",
    paymentReference: "",
    notes: "",
    selectedBankAccountID: "",
  });
  const [proofFile, setProofFile] = useState(null);

  const [showUploadProofModal, setShowUploadProofModal] = useState(false);
  const [uploadProofTxn, setUploadProofTxn] = useState(null);
  const [lateProofFile, setLateProofFile] = useState(null);

  const compExpireDate = profileData?.compExpireDate ?? profileData?.CompExpireDate ?? null;

  const isAccessExpired = compExpireDate
    ? moment(compExpireDate).isBefore(moment(), "day")
    : false;

  const handleLogout = () => {
    dispatch(logout());
    navigate("/user/login", { replace: true });
  };

  const fetchSupportInfo = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_SUPPORT_INFO);
      if (response.data?.status && response.data?.data) {
        setSupportInfo(response.data.data);
      }
    } catch {
      // Support contact is optional on this page; keep UI usable without it.
    }
  }, []);

  const getReservedPendingAmount = (transactions) => {
    if (!Array.isArray(transactions)) return 0;
    return transactions
      .filter((t) => (t.status || "").trim().toLowerCase() === "pending")
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
  };

  /** Balance after confirmed payments and pending submissions (matches API reservation). */
  const getBalanceDue = (invoice, transactions) => {
    if (!invoice) return 0;
    const gross = Math.max(
      0,
      parseFloat(invoice.totalAmount) - parseFloat(invoice.paidAmount || 0)
    );
    const txns = transactions ?? invoice._transactions ?? [];
    return Math.max(0, gross - getReservedPendingAmount(txns));
  };

  const isInvoicePayable = (invoice, transactions) => {
    if (!invoice) return false;
    if (invoice.status === "Cancelled") return false;
    if (invoice.paymentStatus === "Paid") return false;
    if (isInvoicePastDue(invoice)) return false;
    return getBalanceDue(invoice, transactions) > 0;
  };

  const isInvoicePastDue = (invoice) => {
    if (!invoice?.dueDate) return false;
    return moment().startOf("day").isAfter(moment(invoice.dueDate).startOf("day"));
  };

  const getProofUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    const base = apiBaseUrl?.replace(/\/$/, "") || "";
    return `${base}/${path.replace(/^\//, "")}`;
  };

  const hasProofDocument = (txn) => !!txnField(txn, "paymentProofUrl", "PaymentProofUrl");

  const canUploadProofLater = (txn) => {
    const status = (txn.status || "").trim().toLowerCase();
    if (status !== "pending") return false;
    const proofStatus = (txn.proofStatus || "None").trim();
    const proofUrl = txnField(txn, "paymentProofUrl", "PaymentProofUrl");
    if (proofStatus === "Rejected") return true;
    if (!proofUrl || proofStatus === "None") return true;
    return false;
  };

  const needsProofForTxn = (txn) => {
    if (isBankTransfer(txn)) return true;
    const status = (txn.status || "").trim().toLowerCase();
    return status === "pending";
  };

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_INVOICES);
      if (response.data.status && Array.isArray(response.data.data)) {
        const invoiceData = response.data.data;
        const enriched = await Promise.all(
          invoiceData.map(async (inv) => {
            try {
              const txnRes = await axiosInstance.get(APIs.GET_TRANSACTIONS_BY_INVOICE, {
                params: { invoiceID: inv.invoiceID },
              });
              return {
                ...inv,
                _transactions:
                  txnRes.data.status && Array.isArray(txnRes.data.data)
                    ? txnRes.data.data
                    : [],
              };
            } catch {
              return { ...inv, _transactions: [] };
            }
          })
        );
        setInvoices(enriched);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      showErrorToast(
        error.response?.data?.message || "Failed to load billing invoices"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPaymentOptions = useCallback(async () => {
    try {
      const [stripeRes, banksRes] = await Promise.all([
        axiosInstance.get(APIs.GET_STRIPE_PUBLISHABLE_KEY),
        axiosInstance.get(APIs.GET_ACTIVE_BILLING_BANK_ACCOUNTS),
      ]);
      if (stripeRes.data.status && stripeRes.data.data) {
        setStripeEnabled(!!stripeRes.data.data.stripeEnabled);
      }
      if (banksRes.data.status && Array.isArray(banksRes.data.data)) {
        setBankAccounts(banksRes.data.data);
      }
    } catch (error) {
      console.error("Error loading payment options:", error);
    }
  }, []);

  useEffect(() => {
    fetchSupportInfo();
  }, [fetchSupportInfo]);

  useEffect(() => {
    if (isChildBranch) {
      return;
    }
    fetchInvoices();
    fetchPaymentOptions();
  }, [isChildBranch, fetchInvoices, fetchPaymentOptions]);

  useEffect(() => {
    if (isChildBranch) {
      return;
    }
    const paymentResult = searchParams.get("payment");
    if (paymentResult === "success" || searchParams.get("session_id")) {
      setSearchParams({}, { replace: true });
      showSuccessToast("Payment processed. Refreshing invoices…");
      fetchInvoices();
    } else if (paymentResult === "cancelled") {
      showErrorToast("Payment was cancelled.");
      setSearchParams({}, { replace: true });
    }
  }, [isChildBranch, searchParams, setSearchParams, fetchInvoices]);

  const fetchInvoiceDetails = async (invoiceID, keepModalOpen = false) => {
    try {
      setLoading(true);
      const [invoiceRes, txnRes] = await Promise.all([
        axiosInstance.get(APIs.GET_INVOICE_BY_ID, { params: { invoiceID } }),
        axiosInstance.get(APIs.GET_TRANSACTIONS_BY_INVOICE, { params: { invoiceID } }),
      ]);

      if (invoiceRes.data.status) {
        const txns =
          txnRes.data.status && Array.isArray(txnRes.data.data) ? txnRes.data.data : [];
        setSelectedInvoice({ ...invoiceRes.data.data, _transactions: txns });
        setInvoiceTransactions(txns);
        if (!keepModalOpen) setShowDetails(true);
      } else {
        showErrorToast("Failed to load invoice details");
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      if (error.response?.status === 403) {
        showErrorToast("You do not have access to this invoice");
      } else {
        showErrorToast("Failed to load invoice details");
      }
    } finally {
      setLoading(false);
    }
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedInvoice(null);
    setInvoiceTransactions([]);
  };

  const openPayModal = (invoice) => {
    const balance = getBalanceDue(invoice);
    setPayInvoice(invoice);
    setPayTab(stripeEnabled ? "card" : "bank");
    setBankForm({
      amount: balance.toFixed(2),
      paymentReference: invoice.invoiceNumber || "",
      notes: "",
      selectedBankAccountID: bankAccounts[0]?.bankAccountID?.toString() || "",
    });
    setProofFile(null);
    setShowPayModal(true);
  };

  const openUploadProofModal = (txn) => {
    setUploadProofTxn(txn);
    setLateProofFile(null);
    setShowUploadProofModal(true);
  };

  const handleProofFileSelect = (e, setFile) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setFile(null);
      return;
    }

    const isPdf =
      file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
    if (isPdf) {
      setFile(file);
      return;
    }

    const validation = validateImageFile(file, { includeFileName: false });
    if (!validation.valid) {
      showErrorToast(validation.message || `Image must be ${MAX_IMAGE_SIZE_LABEL} or smaller`);
      e.target.value = "";
      setFile(null);
      return;
    }

    setFile(file);
  };

  const handleStripePay = async () => {
    if (!payInvoice) return;
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_CREATE_STRIPE_CHECKOUT, {
        invoiceID: payInvoice.invoiceID,
      });
      if (response.data.status && response.data.data?.checkoutUrl) {
        window.location.href = response.data.data.checkoutUrl;
        return;
      }
      showErrorToast(response.data.message || "Failed to start card payment");
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to start card payment");
    } finally {
      setLoading(false);
    }
  };

  const handleBankPay = async () => {
    if (!payInvoice) return;
    const amount = parseFloat(bankForm.amount);
    if (!amount || amount <= 0) {
      showErrorToast("Please enter a valid amount");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("invoiceID", String(payInvoice.invoiceID));
      formData.append("amount", String(amount));
      formData.append("paymentReference", bankForm.paymentReference || "");
      formData.append("notes", bankForm.notes || "");
      if (bankForm.selectedBankAccountID) {
        formData.append("selectedBankAccountID", String(parseInt(bankForm.selectedBankAccountID, 10)));
      }
      if (proofFile) {
        formData.append("proofFile", proofFile);
      }

      const recordRes = await axiosInstance.post(APIs.POST_RECORD_BANK_TRANSFER, formData);

      if (recordRes.status < 400 && recordRes.data?.status) {
        showSuccessToast(recordRes.data?.message || "Bank transfer submitted.");
        setShowPayModal(false);
        setPayInvoice(null);
        setProofFile(null);
        fetchInvoices();
        if (showDetails && selectedInvoice?.invoiceID === payInvoice.invoiceID) {
          fetchInvoiceDetails(payInvoice.invoiceID, true);
        }
      } else {
        showErrorToast(recordRes.data?.message || "Failed to submit bank transfer");
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || error.message || "Failed to submit bank payment");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadProofLater = async () => {
    const txnId = txnField(uploadProofTxn, "transactionID", "TransactionID");
    if (!txnId) {
      showErrorToast("Invalid payment record");
      return;
    }
    if (!lateProofFile) {
      showErrorToast("Please choose a proof file to upload");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("proofFile", lateProofFile);
      const uploadRes = await axiosInstance.post(APIs.POST_UPLOAD_PAYMENT_PROOF, formData, {
        params: { transactionID: txnId },
      });

      if (uploadRes.status < 400 && uploadRes.data?.status) {
        showSuccessToast(uploadRes.data?.message || "Payment proof uploaded — awaiting approval.");
        setShowUploadProofModal(false);
        setUploadProofTxn(null);
        setLateProofFile(null);
        if (selectedInvoice?.invoiceID) {
          fetchInvoiceDetails(selectedInvoice.invoiceID, true);
        }
        fetchInvoices();
      } else {
        showErrorToast(uploadRes.data?.message || "Failed to upload payment proof");
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to upload payment proof");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      Draft: "secondary",
      Sent: "info",
      Paid: "success",
      PartiallyPaid: "warning",
      Overdue: "danger",
      Cancelled: "dark",
    };
    return <span className={`badge bg-${statusMap[status] || "secondary"}`}>{status}</span>;
  };

  const getPaymentStatusBadge = (status) => {
    const statusMap = {
      Pending: "warning",
      Partial: "info",
      Paid: "success",
      Refunded: "dark",
    };
    return <span className={`badge bg-${statusMap[status] || "secondary"}`}>{status}</span>;
  };

  const getTxnStatusBadge = (status) => {
    const map = {
      Success: "success",
      Pending: "warning",
      Failed: "danger",
      Refunded: "dark",
    };
    return <span className={`badge bg-${map[status] || "secondary"}`}>{status}</span>;
  };

  const renderProofCell = (txn) => {
    const proofUrl = txnField(txn, "paymentProofUrl", "PaymentProofUrl");
    const proofStatus = (txn.proofStatus || "None").trim();
    const canUpload = canUploadProofLater(txn);
    const showProofUi = needsProofForTxn(txn) || hasProofDocument(txn);

    if (!showProofUi) {
      return <span className="text-muted small">—</span>;
    }

    const isPdf = proofUrl?.toLowerCase().endsWith(".pdf");

    return (
      <div className="billing-proof-actions d-flex flex-wrap align-items-center gap-2">
        {proofUrl && (
          <a
            href={getProofUrl(proofUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-primary"
            title="Open payment proof"
          >
            <i className={`fa ${isPdf ? "fa-file-pdf" : "fa-eye"} me-1`} />
            View
          </a>
        )}
        {canUpload && (
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            onClick={() => openUploadProofModal(txn)}
            title="Upload payment proof"
          >
            <i className="fa fa-upload me-1" />
            Upload
          </button>
        )}
        {!proofUrl && !canUpload && proofStatus === "Approved" && (
          <span className="proof-chip proof-approved">
            <i className="fa fa-check" /> Approved
          </span>
        )}
        {!proofUrl && canUpload && (
          <span className="proof-chip proof-missing">
            <i className="fa fa-exclamation-circle" /> Not uploaded
          </span>
        )}
        {proofUrl && proofStatus === "Pending" && (
          <span className="proof-chip proof-pending">
            <i className="fa fa-clock" /> Awaiting approval
          </span>
        )}
        {proofStatus === "Rejected" && (
          <span className="proof-chip proof-rejected">
            <i className="fa fa-times" /> Rejected
          </span>
        )}
        {proofUrl && proofStatus === "Approved" && (
          <span className="proof-chip proof-approved">
            <i className="fa fa-check" /> Approved
          </span>
        )}
      </div>
    );
  };

  if (isChildBranch) {
    return (
      <BranchBillingRestrictedMessage
        profileData={profileData}
        themeColor={themeColor}
        isRestricted={isRestricted}
        onLogout={handleLogout}
        supportInfo={supportInfo}
      />
    );
  }

  const balanceDue = selectedInvoice
    ? getBalanceDue(selectedInvoice, invoiceTransactions)
    : 0;
  const currency = selectedInvoice?.currency || "";

  return (
    <div className={`company-billing-page${isRestricted ? " company-billing-page--restricted" : ""}`}>
      {loading && <Preloader />}

      {isRestricted && (
        <BillingOnlyHeader
          profileData={profileData}
          themeColor={themeColor}
          onLogout={handleLogout}
        />
      )}

      <div className="container-fluid px-4 py-4">
        {!isRestricted && (
          <div className="d-flex align-items-center mb-3">
            <div>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <a href="#/">Home</a>
                </li>
                <li className="breadcrumb-item">Administration</li>
                <li className="breadcrumb-item active">Billing</li>
              </ol>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-12">
            <h4 className="mb-3 fw-bold">Billing</h4>

            {isRestricted && (
              <div className="alert alert-danger billing-restricted-alert mb-4" role="alert">
                <i className="fa fa-exclamation-triangle me-2" aria-hidden />
                Your subscription and grace period have ended. Pay an outstanding invoice below to
                restore full access to BackOffice.
              </div>
            )}

            {isRestricted && (
              <div className="mb-4">
                <SupportContactBlock supportInfo={supportInfo} themeColor={themeColor} />
              </div>
            )}

            <div
              className={`card mb-4 billing-access-card ${
                isRestricted || isAccessExpired ? "border-danger" : "border-success"
              }`}
            >
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 44,
                      height: 44,
                      backgroundColor:
                        isRestricted || isAccessExpired
                          ? "rgba(220,53,69,0.12)"
                          : "rgba(25,135,84,0.12)",
                      color: isRestricted || isAccessExpired ? "#dc3545" : "#198754",
                    }}
                  >
                    <i
                      className={`fa fa-${
                        isRestricted || isAccessExpired ? "exclamation-triangle" : "check-circle"
                      } fa-lg`}
                    />
                  </div>
                  <div>
                    <h6 className="card-title mb-1 fw-bold">Subscription access</h6>
                    {compExpireDate ? (
                      <>
                        <p className="mb-0">
                          {isRestricted ? (
                            <span className="text-danger fw-semibold">
                              Access limited — subscription expired on{" "}
                              {moment(compExpireDate).format("MMMM D, YYYY")} and the grace period
                              has ended.
                            </span>
                          ) : isAccessExpired ? (
                            <span className="text-danger fw-semibold">
                              Access expired on {moment(compExpireDate).format("MMMM D, YYYY")}.
                            </span>
                          ) : (
                            <span className="text-success fw-semibold">
                              Active until {moment(compExpireDate).format("MMMM D, YYYY")}.
                            </span>
                          )}
                        </p>
                        <p className="text-muted small mt-2 mb-0">
                          Access extends when an invoice is paid by card or when a bank transfer proof is approved.
                        </p>
                      </>
                    ) : (
                      <p className="text-muted mb-0 small">No expiry date on file. Contact support if needed.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="card billing-invoices-card">
              <div className="card-header d-flex justify-content-between align-items-center py-3">
                <span className="fw-bold">Invoices ({invoices.length})</span>
              </div>
              <div className="card-body">
                {invoices.length === 0 ? (
                  <div className="alert alert-info mb-0">
                    <i className="fa fa-info-circle me-2" />
                    No invoices yet. Contact support if you believe this is an error.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>Invoice #</th>
                          <th>Service period</th>
                          <th>Due date</th>
                          <th className="text-end">Total</th>
                          <th className="text-end">Paid</th>
                          <th className="text-center">Status</th>
                          <th className="text-center">Payment</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice) => (
                          <tr key={invoice.invoiceID}>
                            <td>
                              <strong>{invoice.invoiceNumber}</strong>
                              {isInvoicePastDue(invoice) && invoice.paymentStatus !== "Paid" && (
                                <span className="badge bg-secondary ms-1">Past due</span>
                              )}
                            </td>
                            <td className="text-muted small">
                              {invoice.servicePeriodStart
                                ? `${moment(invoice.servicePeriodStart).format("MMM D, YYYY")} – ${moment(invoice.servicePeriodEnd).format("MMM D, YYYY")}`
                                : "—"}
                            </td>
                            <td>{moment(invoice.dueDate).format("MMM D, YYYY")}</td>
                            <td className="text-end fw-semibold">
                              {invoice.currency} {parseFloat(invoice.totalAmount).toFixed(2)}
                            </td>
                            <td className="text-end text-success">
                              {invoice.currency} {parseFloat(invoice.paidAmount || 0).toFixed(2)}
                            </td>
                            <td className="text-center">{getStatusBadge(invoice.status)}</td>
                            <td className="text-center">
                              {getPaymentStatusBadge(invoice.paymentStatus)}
                            </td>
                            <td className="text-center text-nowrap">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary me-1"
                                onClick={() => fetchInvoiceDetails(invoice.invoiceID)}
                                title="View details"
                              >
                                <i className="fa fa-eye" />
                              </button>
                              {isInvoicePayable(invoice) && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => openPayModal(invoice)}
                                  title="Pay invoice"
                                  style={{ backgroundColor: themeColor, borderColor: themeColor }}
                                >
                                  Pay
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        isOpen={showPayModal}
        onClose={() => {
          setShowPayModal(false);
          setPayInvoice(null);
          setProofFile(null);
        }}
        title={`Pay invoice ${payInvoice?.invoiceNumber || ""}`}
        onSubmit={payTab === "card" ? handleStripePay : handleBankPay}
        submitButtonText={payTab === "card" ? "Pay with card" : "Submit bank transfer"}
        showButton={true}
        modalSize="modal-lg"
        maxHeight="90vh"
        isCentered
        zIndex={MODAL_Z_PAY}
      >
        {payInvoice && (
          <>
            <div
              className="rounded-3 px-3 py-2 mb-3"
              style={{ backgroundColor: "rgba(0,0,0,0.04)" }}
            >
              <span className="text-muted small">Balance due</span>
              <div className="fw-bold fs-5">
                {payInvoice.currency} {getBalanceDue(payInvoice).toFixed(2)}
              </div>
            </div>

            <ul className="nav nav-tabs billing-pay-tabs mb-3">
              {stripeEnabled && (
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${payTab === "card" ? "active" : ""}`}
                    onClick={() => setPayTab("card")}
                  >
                    <i className="fa fa-credit-card me-1" /> Card (Stripe)
                  </button>
                </li>
              )}
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${payTab === "bank" ? "active" : ""}`}
                  onClick={() => setPayTab("bank")}
                >
                  <i className="fa fa-university me-1" /> Bank transfer
                </button>
              </li>
            </ul>

            {payTab === "card" && stripeEnabled && (
              <p className="mb-0 text-muted">
                You will be redirected to Stripe Checkout. Access is extended automatically once payment is confirmed.
              </p>
            )}

            {payTab === "bank" && (
              <div>
                {bankAccounts.length === 0 ? (
                  <div className="alert alert-warning mb-0">
                    No bank accounts are configured. Please contact support.
                  </div>
                ) : (
                  <>
                    <p className="billing-section-title mb-2">Transfer to</p>
                    {bankAccounts.map((bank) => (
                      <div key={bank.bankAccountID} className="billing-bank-card card mb-2">
                        <div className="card-body py-2 px-3">
                          <strong>{bank.bankName}</strong>
                          {bank.branchName && (
                            <span className="text-muted small"> — {bank.branchName}</span>
                          )}
                          <div className="small mt-1 text-muted">
                            <div>Account: {bank.accountTitle}</div>
                            <div># {bank.accountNumber}</div>
                            {bank.iban && <div>IBAN: {bank.iban}</div>}
                            {bank.swiftCode && <div>SWIFT: {bank.swiftCode}</div>}
                            {bank.instructions && <div className="mt-1 fst-italic">{bank.instructions}</div>}
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="row g-3 mt-1">
                      <div className="col-md-6">
                        <label className="form-label">Amount ({payInvoice.currency})</label>
                        <input
                          type="number"
                          className="form-control"
                          value={bankForm.amount}
                          onChange={(e) => setBankForm({ ...bankForm, amount: e.target.value })}
                          step="0.01"
                          min="0"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Payment reference</label>
                        <input
                          type="text"
                          className="form-control"
                          value={bankForm.paymentReference}
                          onChange={(e) =>
                            setBankForm({ ...bankForm, paymentReference: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Notes (optional)</label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={bankForm.notes}
                          onChange={(e) => setBankForm({ ...bankForm, notes: e.target.value })}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">
                          Payment proof <span className="text-muted fw-normal">(optional now)</span>
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(e) => handleProofFileSelect(e, setProofFile)}
                        />
                        <p className="proof-upload-hint mb-0">
                          JPG/PNG max {MAX_IMAGE_SIZE_LABEL}, or PDF. You can upload proof later from invoice details if needed.
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </CustomModal>

      <CustomModal
        isOpen={showDetails && !!selectedInvoice}
        onClose={closeDetails}
        title={selectedInvoice ? `Invoice ${selectedInvoice.invoiceNumber}` : "Invoice"}
        showButton={true}
        showSubmitButton={false}
        showCancelButton={true}
        modalSize="modal-xl"
        maxHeight="92vh"
        isCentered
        zIndex={MODAL_Z_INVOICE_DETAILS}
      >
        {selectedInvoice && (
          <div className="billing-invoice-detail">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div className="d-flex flex-wrap gap-2">
                {getStatusBadge(selectedInvoice.status)}
                {getPaymentStatusBadge(selectedInvoice.paymentStatus)}
              </div>
              {isInvoicePayable(selectedInvoice, invoiceTransactions) && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    closeDetails();
                    openPayModal(selectedInvoice);
                  }}
                  style={{ backgroundColor: themeColor, borderColor: themeColor }}
                >
                  <i className="fa fa-credit-card me-1" />
                  Pay invoice
                </button>
              )}
            </div>

              {isInvoicePastDue(selectedInvoice) && selectedInvoice.paymentStatus !== "Paid" && (
                <div className="alert alert-warning py-2 mb-3">
                  Payment deadline passed on{" "}
                  {moment(selectedInvoice.dueDate).format("MMM D, YYYY")}. Contact support to extend the
                  due date or arrange payment.
                </div>
              )}
            <div className="billing-meta-row">
              <span className="meta-item">
                <i className="fa fa-calendar" />
                {selectedInvoice.servicePeriodStart
                  ? `${moment(selectedInvoice.servicePeriodStart).format("MMM D, YYYY")} – ${moment(selectedInvoice.servicePeriodEnd).format("MMM D, YYYY")}`
                  : "No service period"}
              </span>
              <span className="meta-item">
                <i className="fa fa-clock" />
                Due {moment(selectedInvoice.dueDate).format("MMM D, YYYY")}
              </span>
            </div>

            <div className="billing-stat-grid">
              <div className="billing-stat-card">
                <div className="stat-label">Total</div>
                <p className="stat-value">
                  {currency} {parseFloat(selectedInvoice.totalAmount).toFixed(2)}
                </p>
              </div>
              <div className="billing-stat-card stat-paid">
                <div className="stat-label">Paid</div>
                <p className="stat-value">
                  {currency} {parseFloat(selectedInvoice.paidAmount || 0).toFixed(2)}
                </p>
              </div>
              <div className="billing-stat-card stat-balance">
                <div className="stat-label">Balance due</div>
                <p className="stat-value">
                  {currency} {balanceDue.toFixed(2)}
                </p>
              </div>
              <div className="billing-stat-card">
                <div className="stat-label">Subtotal</div>
                <p className="stat-value">
                  {currency} {parseFloat(selectedInvoice.subtotalAmount || 0).toFixed(2)}
                </p>
              </div>
            </div>

            {selectedInvoice.notes && (
              <div className="mb-3 p-3 rounded-3 border bg-light">
                <div className="billing-section-title mb-1">Notes</div>
                <p className="mb-0 text-muted">{selectedInvoice.notes}</p>
              </div>
            )}

            {selectedInvoice.items?.length > 0 && (
              <div className="mb-4">
                <div className="billing-section-title">Line items</div>
                <div className="billing-table-wrap">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.items.map((item) => (
                        <tr key={item.invoiceItemID}>
                          <td>{item.itemDescription}</td>
                          <td className="text-end fw-semibold">
                            {currency} {parseFloat(item.totalPrice).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="billing-section-title">Payment history</div>
            {invoiceTransactions.length === 0 ? (
              <p className="text-muted small mb-0">No payments recorded yet.</p>
            ) : (
              <div className="billing-table-wrap">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Method</th>
                      <th className="text-end">Amount</th>
                      <th>Status</th>
                      <th>Proof</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceTransactions.map((txn) => (
                      <tr key={txn.transactionID ?? txn.TransactionID}>
                        <td className="text-nowrap">
                          {moment(txn.transactionDate).format("MMM D, YYYY")}
                        </td>
                        <td>{txn.paymentMethod}</td>
                        <td className="text-end fw-semibold">
                          {txn.currency} {parseFloat(txn.amount).toFixed(2)}
                        </td>
                        <td>{getTxnStatusBadge(txn.status)}</td>
                        <td>{renderProofCell(txn)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CustomModal>

      {showUploadProofModal &&
        createPortal(
          <CustomModal
            isOpen={showUploadProofModal}
            onClose={() => {
              setShowUploadProofModal(false);
              setUploadProofTxn(null);
              setLateProofFile(null);
            }}
            title="Upload payment proof"
            subtitle={
              uploadProofTxn
                ? `${uploadProofTxn.paymentMethod} · ${uploadProofTxn.currency} ${parseFloat(uploadProofTxn.amount).toFixed(2)} · ${moment(uploadProofTxn.transactionDate).format("MMM D, YYYY")}`
                : ""
            }
            onSubmit={handleUploadProofLater}
            submitButtonText="Upload proof"
            showButton={true}
            modalSize="modal-md"
            maxHeight="85vh"
            isCentered
            zIndex={MODAL_Z_UPLOAD_PROOF}
          >
            <div className="alert alert-light border small mb-3">
              <i className="fa fa-info-circle me-2 text-primary" />
              Upload a screenshot or PDF of your payment. Our team will review it and extend your access once approved.
            </div>
            <label className="form-label fw-semibold">
              Proof file (JPG/PNG max {MAX_IMAGE_SIZE_LABEL}, or PDF)
            </label>
            <input
              type="file"
              className="form-control"
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={(e) => handleProofFileSelect(e, setLateProofFile)}
            />
            {lateProofFile && (
              <p className="small text-muted mt-2 mb-0">
                <i className="fa fa-paperclip me-1" />
                {lateProofFile.name}
              </p>
            )}
          </CustomModal>,
          document.body
        )}
    </div>
  );
}

export default CompanyBilling;
